import { IComboBoxOption, IDetailsRowProps, IDropdownOption, MessageBarType } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import {
  BaseDialog,
  CommonStyles,
  debounce,
  detailsHeaderStylesBasic,
  DetailsList,
  detailsListStylesCard,
  Dropdown,
  EmptyMessage,
  getDescriptionForItemCount,
  getItemsWithEmptyItemsOfNextPage,
  IColumnWithDataType,
  IDetailsListProps,
  ISearchProps,
  Link,
  linkStylesGhost,
  linkStylesPrimary,
  ListRow,
  Loader,
  loaderStylesSpinnerXLarge,
  MessageBar,
  Search,
  useClassNames,
  useToast,
} from '@h2oai/ui-kit';
import { CSSProperties, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { User } from '../../authz/gen/ai/h2o/user/v1/user_pb';
import { fsApi } from '../../featurestore/api';
import { useQueryActiveUser } from '../../featurestore/featurestore.hooks';
import {
  ActivePermission,
  CoreService,
  GetActivePermissionResponse,
  GetUserPermissionsResponse,
  PermissionType,
  ResourceType,
  UserWithPermission,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { AccessInfo, ContentsTitle, defaultPageSize, permissionTypeNameMap } from '../../featurestore/models';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import { GrantPermissionPanel } from '../GrantPermissionPanel/GrantPermissionPanel';
import { accessListStylesDefault, IAccessListClassNames, IAccessListStyles } from './AccessList.styles';

const activePermissionTypeMap = new Map<ActivePermission, PermissionType | undefined>([
  [ActivePermission.ACTIVE_PERMISSION_CONSUMER, PermissionType.Consumer],
  [ActivePermission.ACTIVE_PERMISSION_EDITOR, PermissionType.Editor],
  [ActivePermission.ACTIVE_PERMISSION_NONE, undefined],
  [ActivePermission.ACTIVE_PERMISSION_OWNER, PermissionType.Owner],
  [ActivePermission.ACTIVE_PERMISSION_SENSITIVE_CONSUMER, PermissionType.SensitiveConsumer],
  [ActivePermission.ACTIVE_PERMISSION_VIEWER, PermissionType.Viewer],
]);

enum RequestAction {
  revoke = 'Revoke',
  requestHigherPermission = 'Request Higher Permission',
  promote = 'Promote',
  grant = 'Grant',
}

const getColumns = (
  title: ContentsTitle,
  onAction: (action: RequestAction, item: AccessInfo) => void,
  listResourceType?: ResourceType,
  user?: User
): IColumnWithDataType[] => {
  const columns: IColumnWithDataType[] = [
    {
      key: 'name',
      name: 'Name',
      fieldName: 'name',
      minWidth: 150,
      data: {
        listCellProps: {
          iconProps: { iconName: 'DocumentSet' },
        },
      },
    },
    {
      key: 'email',
      name: 'Email',
      fieldName: 'email',
      minWidth: 200,
    },
    {
      key: 'AccessType',
      name: 'Access Type',
      fieldName: 'permissionName',
      minWidth: 200,
      data: {
        listCellProps: {
          onRenderText: (item: AccessInfo) => {
            return (
              <div style={{ height: '18px' }}>
                <div style={{ position: 'absolute' }}>{item.permissionName}</div>
              </div>
            );
          },
        },
      },
    },
    {
      key: 'actions',
      name: 'Actions',
      minWidth: 260,
      maxWidth: 260,
      onRender: (item: AccessInfo) => {
        const { permission, email, resourceType, permissionName } = item;
        const isOwner = permission === PermissionType.Owner;
        const isCurrentUser = Boolean(
          title === ContentsTitle.accessList && email && user?.emails?.some((e) => e.address === email)
        );
        const action =
          title === ContentsTitle.accessList ? RequestAction.revoke : RequestAction.requestHigherPermission;
        const hasPromoteButton = title === ContentsTitle.accessList && !isOwner;
        const inheritedProjectPermission =
          listResourceType === ResourceType.FEATURE_SET &&
          resourceType === ResourceType.PROJECT &&
          permissionName !== 'No Permission';
        return inheritedProjectPermission ? (
          <></>
        ) : (
          <div
            style={{
              ...(CommonStyles.flexCenterVertically as CSSProperties),
              alignItems: 'flex-end',
              textAlign: 'right',
              width: '100%',
            }}
          >
            <div style={{ display: 'flex', gap: '8px' }}>
              {hasPromoteButton && (
                <Link styles={linkStylesGhost} onClick={() => onAction(RequestAction.promote, item)}>
                  {RequestAction.promote}
                </Link>
              )}
              {!isCurrentUser && (
                <Link styles={linkStylesGhost} onClick={() => onAction(action, item)}>
                  {action}
                </Link>
              )}
            </div>
          </div>
        );
      },
    },
  ];
  return columns;
};

const getPermissionName = (
  resourceType?: ResourceType,
  email?: string,
  permission?: PermissionType,
  listResourceType?: ResourceType,
  currentUserEmail?: string,
  title?: ContentsTitle
) => {
  const messageForInheritedProjectPermission =
    listResourceType === ResourceType.FEATURE_SET && resourceType === ResourceType.PROJECT
      ? ' (Transitively obtained from the project permissions)'
      : '';
  const messageForCurrentUser = title !== ContentsTitle.myAccess && email === currentUserEmail ? ' (Current User)' : '';
  return permission
    ? `${permissionTypeNameMap.get(permission)}${messageForInheritedProjectPermission || messageForCurrentUser}`
    : 'No Permission';
};

const getListItem = (
  item: UserWithPermission,
  listResourceType?: ResourceType,
  currentUserEmail?: string,
  title?: ContentsTitle
): AccessInfo => {
  const { user: { name, email } = {}, resourcePermission: { permission, resourceType } = {} } = item;
  return {
    name,
    email,
    permission,
    resourceType,
    permissionName: getPermissionName(resourceType, email, permission, listResourceType, currentUserEmail, title),
  };
};

const getPermissionTypeOptions = () => {
  const options: IDropdownOption[] = [];
  permissionTypeNameMap.forEach((text, key) => {
    options.push({ key, text });
  });
  return options;
};

interface AccessListData {
  items: AccessInfo[];
  pageToken?: string;
  pageSize?: number;
}

interface IAccessListProps {
  id?: string;
  resourceType?: ResourceType;
  name?: string;
  activePermission?: ActivePermission;
  onUpdateData?: () => void;
  onScroll?: React.UIEventHandler<HTMLDivElement>;
}

export const AccessList = ({ id, resourceType, name, activePermission, onScroll }: IAccessListProps) => {
  const [description, setDescription] = useState('');
  const [filters, setFilters] = useState<PermissionType[]>([]);
  const [data, setData] = useState<AccessListData>({
    items: [],
  });
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [showGrantAccessPanel, setShowGrantAccessPanel] = useState(false);
  const [hideDialog, { toggle: toggleHideDialog }] = useBoolean(true);
  const refMeta = useRef<{
    isLoadingPage: boolean;
    selectedItem?: AccessInfo;
    mounted?: boolean;
    searchText?: string;
    action?: RequestAction;
    requestHigherPermission?: boolean;
  }>({
    isLoadingPage: false,
  });
  const { addToast } = useToast();
  const activeUserQuery = useQueryActiveUser();
  const user = activeUserQuery.data?.user;
  const userRef = useRef(user);
  userRef.current = user;
  const title =
    activePermission === ActivePermission.ACTIVE_PERMISSION_OWNER ? ContentsTitle.accessList : ContentsTitle.myAccess;
  const load = useCallback(
    async ({ items, pageToken }: AccessListData = { items: [] }, hasLoading = true) => {
      hasLoading && setLoadingMessage(`Loading ${title}...`);
      try {
        const pageSize = defaultPageSize;
        let newItems: AccessInfo[] = [];
        const isProject = resourceType === ResourceType.PROJECT;
        if (title === ContentsTitle.accessList) {
          const api = isProject
            ? CoreService.GetUserActiveProjectPermissions
            : CoreService.GetUserActiveFeatureSetPermissions;
          const { users = [], nextPageToken }: GetUserPermissionsResponse = await fsApi(api, {
            resourceId: id,
            permissionFilters: filters,
            query: refMeta.current.searchText,
            pageSize,
            pageToken,
          });
          pageToken = nextPageToken;
          newItems = getItemsWithEmptyItemsOfNextPage(items, users, pageSize, (item: UserWithPermission) =>
            getListItem(item, resourceType, user?.email, title)
          );
        } else {
          // my access
          const api = isProject ? CoreService.GetActiveProjectPermission : CoreService.GetActiveFeatureSetPermission;
          const { permission, resourceType: permissionResourceType }: GetActivePermissionResponse = await fsApi(api, {
            resourceId: id,
          });
          if (permission) {
            const permissionType = activePermissionTypeMap.get(permission);
            const permissionName = getPermissionName(
              permissionResourceType,
              userRef.current?.email,
              permissionType,
              resourceType,
              userRef.current?.email,
              title
            );
            newItems = [
              {
                name: userRef.current?.name,
                email: userRef.current?.email,
                permission: permissionType,
                resourceType: permissionResourceType,
                permissionName,
              },
            ];
          }
        }
        const newData: AccessListData = {
          items: newItems,
          pageToken,
          pageSize,
        };
        if (!refMeta.current.mounted) return;
        setData({
          ...newData,
        });
        setDescription(getDescriptionForItemCount(newItems.length, pageSize, pageToken, title));
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while loading ${title}: `));
      } finally {
        if (!refMeta.current.mounted) return;
        if (hasLoading) setLoadingMessage('');
        refMeta.current.isLoadingPage = false;
      }
    },
    [addToast, filters]
  );
  const onLoad = useMemo(() => debounce(300, load), [load]);
  const onRevoke = useCallback(
    async (item: AccessInfo) => {
      setLoadingMessage(`Request to revoke the user permission...`);
      try {
        const { email, permission } = item;
        const isProject = resourceType === ResourceType.PROJECT;
        const req = isProject
          ? {
              projectId: id,
              userEmails: [email],
              permission,
            }
          : { featureSetId: id, userEmails: [email], permission };
        const api = isProject ? CoreService.RemoveProjectPermission : CoreService.RemoveFeatureSetPermission;
        await fsApi(api, req);
        addToast({
          messageBarType: MessageBarType.success,
          message: `You have successfully revoked access to the selected user`,
        });
        load();
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while applying request: `));
      } finally {
        setLoadingMessage('');
      }
    },
    [addToast, load]
  );
  const onAction = useCallback((action: RequestAction, item: AccessInfo) => {
    refMeta.current.selectedItem = item;
    refMeta.current.action = action;
    if (action === RequestAction.revoke) {
      toggleHideDialog();
    } else {
      setShowGrantAccessPanel(true);
    }
  }, []);
  const onChangeFilters = (_e: FormEvent<HTMLDivElement>, option?: IComboBoxOption) => {
    if (option) {
      const state = option.key as PermissionType;
      if (option.selected) {
        if (!filters.includes(state)) {
          setFilters([...filters, state]);
        }
      } else {
        if (filters.includes(state)) {
          setFilters(filters.filter((s) => s !== state));
        }
      }
    }
  };
  const changeSearchText = useCallback((text: string) => {
    refMeta.current.searchText = text;
    load();
  }, []);
  const onSearchTextChange = useMemo(() => debounce(300, changeSearchText), [changeSearchText]);
  const searchProps: ISearchProps = {
    placeholder: 'Search by name or email',
    onSearchTextChange,
  };
  const detailsListProps: IDetailsListProps = {
    styles: detailsListStylesCard,
    headerStyles: detailsHeaderStylesBasic,
    columns: getColumns(title, onAction, resourceType, user),
    isHeaderVisible: false,
    items: data.items,
    cellStyleProps: {
      cellExtraRightPadding: 0,
      cellLeftPadding: 0,
      cellRightPadding: 0,
    },
    onRenderRow: (props?: IDetailsRowProps) => {
      const { item, columns = [] } = props || {};
      if (item.isNotLoaded && !refMeta.current.isLoadingPage) {
        refMeta.current.isLoadingPage = true;
        onLoad(data, false);
      }
      return <ListRow data={item} columns={columns} loading={item.isNotLoaded} />;
    },
  };
  useEffect(() => {
    refMeta.current.mounted = true;
    if (id) {
      load();
    }
    return () => {
      refMeta.current.mounted = false;
    };
  }, [load, id]);
  const classNames = useClassNames<IAccessListStyles, IAccessListClassNames>('AccessList', accessListStylesDefault);
  return (
    <div className={classNames.root} data-test="AccessList">
      <div className={classNames.description}>
        <div className={classNames.descriptionText}>{description}</div>
        <div className={classNames.filter}>
          <Search style={{ width: '230px' }} {...searchProps} />
          <Dropdown
            placeholder="Filter by status"
            data-test="dropdown-access-request-list-filter"
            multiSelect
            selectedKeys={filters}
            options={getPermissionTypeOptions()}
            onChange={onChangeFilters}
          />
          {activePermission === ActivePermission.ACTIVE_PERMISSION_OWNER && (
            <Link
              styles={[linkStylesGhost, { root: { height: '32px' } }]}
              text="Grant Access"
              onClick={() => {
                refMeta.current.selectedItem = undefined;
                refMeta.current.action = RequestAction.grant;
                setShowGrantAccessPanel(true);
              }}
            >
              {ContentsTitle.grantAccess}
            </Link>
          )}
        </div>
      </div>
      {loadingMessage ? (
        <Loader styles={loaderStylesSpinnerXLarge} label={loadingMessage} />
      ) : (
        <div
          className={classNames.cardView}
          data-is-scrollable="true"
          data-test="access-list-card-view"
          onScroll={onScroll}
        >
          {data.items.length === 0 ? (
            <EmptyMessage message="No Items Available" />
          ) : (
            <DetailsList {...detailsListProps} />
          )}
        </div>
      )}
      <GrantPermissionPanel
        id={id}
        name={name}
        resourceType={resourceType}
        accessInfo={refMeta.current.selectedItem}
        requestHigherPermission={refMeta.current.action === RequestAction.requestHigherPermission}
        promote={refMeta.current.action === RequestAction.promote}
        show={showGrantAccessPanel}
        onDismiss={(updated) => {
          if (updated) {
            load();
          }
          setShowGrantAccessPanel(false);
          refMeta.current.selectedItem = undefined;
          refMeta.current.action = undefined;
        }}
      />
      <BaseDialog
        hidden={hideDialog}
        onDismiss={toggleHideDialog}
        title={`Revoke Permission`}
        content={
          <div>
            <MessageBar messageBarType={MessageBarType.warning}>
              <div>
                Are you sure you want to revoke access to this project?
                <br />
                User <strong>{refMeta.current.selectedItem?.name}</strong> will lose access to the{' '}
                {resourceType === ResourceType.PROJECT ? 'project' : 'feature set'} &quot;
                <strong>{name}</strong>&quot; as &quot;
                <strong>
                  {refMeta.current.selectedItem?.permission
                    ? permissionTypeNameMap.get(refMeta.current.selectedItem?.permission)
                    : ''}
                </strong>
                &quot;
              </div>
            </MessageBar>
          </div>
        }
        footer={
          <>
            <Link styles={[linkStylesGhost, { root: { marginRight: 8 } }]} onClick={toggleHideDialog}>
              Cancel
            </Link>
            <Link
              styles={linkStylesPrimary}
              onClick={() => {
                toggleHideDialog();
                if (refMeta.current.selectedItem) {
                  onRevoke(refMeta.current.selectedItem);
                }
              }}
            >{`Revoke Permission`}</Link>
          </>
        }
      ></BaseDialog>
    </div>
  );
};
